import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Users, Target, Sparkles, FileText, CornerDownLeft } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useLeads } from '@/features/leads/use-leads'
import { useCampaigns } from '@/features/campaigns/use-campaigns'

/**
 * GlobalSearch — top-bar command palette that fuzzy-matches across leads,
 * campaigns, and a few fixed navigation targets.
 *
 * Opens via the top-bar search input, the bell-adjacent Cmd/Ctrl+K shortcut,
 * or mobile bottom-nav search icon. Client-side filtering over the data that
 * React Query has already cached for those pages — no extra API endpoints.
 */

type Kind = 'lead' | 'campaign' | 'nav'

type Result = {
  readonly id: string
  readonly kind: Kind
  readonly title: string
  readonly subtitle?: string
  readonly link: string
}

const NAV_TARGETS: readonly Result[] = [
  { id: 'nav-dashboard', kind: 'nav', title: 'Dashboard', subtitle: 'Workspace overview', link: '/dashboard' },
  { id: 'nav-leads', kind: 'nav', title: 'Leads', subtitle: 'Pipeline', link: '/leads' },
  { id: 'nav-discover', kind: 'nav', title: 'Discover leads', subtitle: 'Find new prospects', link: '/leads/discover' },
  { id: 'nav-campaigns', kind: 'nav', title: 'Campaigns', subtitle: 'Outreach', link: '/campaigns' },
  { id: 'nav-content', kind: 'nav', title: 'Content', subtitle: 'Posts', link: '/content' },
  { id: 'nav-inbox', kind: 'nav', title: 'Inbox', subtitle: 'Conversations', link: '/inbox' },
  { id: 'nav-settings', kind: 'nav', title: 'Settings', subtitle: 'Configuration', link: '/settings' },
]

const KIND_ICON: Record<Kind, React.ComponentType<{ className?: string }>> = {
  lead: Users,
  campaign: Target,
  nav: Sparkles,
}

const KIND_LABEL: Record<Kind, string> = {
  lead: 'LEADS',
  campaign: 'CAMPAIGNS',
  nav: 'NAVIGATE',
}

function matches(haystack: string | undefined | null, needle: string): boolean {
  if (!haystack) return false
  return haystack.toLowerCase().includes(needle.toLowerCase())
}

type GlobalSearchProps = {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  // Only fetch when the palette is open; React Query caches so reopen is cheap.
  const { data: leads = [] } = useLeads({})
  const { data: campaigns = [] } = useCampaigns({})

  // Reset state when the dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('')
      setActiveIndex(0)
    } else {
      // Focus the input on open
      setTimeout(() => inputRef.current?.focus(), 30)
    }
  }, [open])

  const results: readonly Result[] = useMemo(() => {
    const q = query.trim()
    const showAllNav = q.length === 0

    const leadMatches: Result[] = leads
      .filter((l) =>
        showAllNav
          ? false
          : matches(l.full_name, q) ||
            matches(`${l.first_name ?? ''} ${l.last_name ?? ''}`, q) ||
            matches(l.company, q) ||
            matches(l.job_title, q),
      )
      .slice(0, 6)
      .map((l) => ({
        id: l.id,
        kind: 'lead' as const,
        title: l.full_name ?? `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim() ?? 'Lead',
        subtitle: [l.job_title, l.company].filter(Boolean).join(' · ') || undefined,
        link: `/leads?id=${encodeURIComponent(l.id)}`,
      }))

    const campaignMatches: Result[] = campaigns
      .filter((c) =>
        showAllNav
          ? false
          : matches(c.name, q) || matches(c.status, q),
      )
      .slice(0, 4)
      .map((c) => ({
        id: c.id,
        kind: 'campaign' as const,
        title: c.name,
        subtitle: `${c.status} · ${c.leads_count} leads`,
        link: `/campaigns?id=${encodeURIComponent(c.id)}`,
      }))

    const navMatches: Result[] = showAllNav
      ? [...NAV_TARGETS]
      : NAV_TARGETS.filter((n) => matches(n.title, q) || matches(n.subtitle, q))

    return [...leadMatches, ...campaignMatches, ...navMatches]
  }, [query, leads, campaigns])

  useEffect(() => {
    // Clamp active index when result set shrinks
    if (activeIndex >= results.length) setActiveIndex(Math.max(0, results.length - 1))
  }, [results.length, activeIndex])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(results.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const r = results[activeIndex]
      if (r) selectResult(r)
    }
  }

  function selectResult(r: Result) {
    onOpenChange(false)
    setTimeout(() => navigate(r.link), 100)
  }

  // Group results by kind for headers
  const grouped = useMemo(() => {
    const out: Array<{ kind: Kind; items: Result[] }> = []
    let current: { kind: Kind; items: Result[] } | null = null
    for (let i = 0; i < results.length; i++) {
      const r = results[i]!
      if (!current || current.kind !== r.kind) {
        current = { kind: r.kind, items: [r] }
        out.push(current)
      } else {
        current.items.push(r)
      }
    }
    return out
  }, [results])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-[var(--color-silver)]" />
          <Input
            ref={inputRef}
            placeholder="Search leads, campaigns, navigation…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIndex(0)
            }}
            onKeyDown={handleKeyDown}
            className="h-auto border-0 bg-transparent p-0 text-sm focus-visible:ring-0"
            autoFocus
          />
          <kbd className="hidden shrink-0 rounded border border-border bg-[rgba(255,255,255,0.04)] px-1.5 py-0.5 text-[10px] font-mono text-[var(--color-silver)] md:inline-flex">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[min(420px,calc(100dvh-8rem))] overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-10 text-center">
              <p className="text-sm text-[var(--color-silver-bright)]">No matches</p>
              <p className="text-xs text-[var(--color-silver)]">
                Try a lead name, campaign, or a page like "inbox".
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {grouped.map((group) => {
                const Icon = KIND_ICON[group.kind]
                return (
                  <div key={group.kind} className="space-y-0.5">
                    <p className="px-4 pb-1 text-[10px] font-medium uppercase tracking-wider text-[var(--color-silver)]">
                      {KIND_LABEL[group.kind]}
                    </p>
                    {group.items.map((r) => {
                      const isActive =
                        results.indexOf(r) === activeIndex
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onMouseEnter={() => setActiveIndex(results.indexOf(r))}
                          onClick={() => selectResult(r)}
                          className={cn(
                            'flex w-full items-center gap-3 px-4 py-2 text-left transition-colors',
                            isActive
                              ? 'bg-[rgba(28,63,255,0.12)] text-[var(--color-silver-bright)]'
                              : 'hover:bg-[rgba(255,255,255,0.03)] text-[var(--color-silver-bright)]',
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[rgba(255,255,255,0.04)]',
                              isActive && 'bg-[rgba(28,63,255,0.18)]',
                            )}
                          >
                            <Icon className="h-3.5 w-3.5 text-[var(--color-cobalt-bright)]" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm">{r.title}</p>
                            {r.subtitle && (
                              <p className="truncate text-xs text-[var(--color-silver)]">
                                {r.subtitle}
                              </p>
                            )}
                          </div>
                          {isActive && (
                            <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-[var(--color-silver)]" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-4 py-2 text-[10px] uppercase tracking-wide text-[var(--color-silver)]">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-[rgba(255,255,255,0.04)] px-1 font-mono text-[10px]">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-[rgba(255,255,255,0.04)] px-1 font-mono text-[10px]">↵</kbd>
              open
            </span>
          </span>
          <span className="flex items-center gap-1.5">
            <FileText className="h-3 w-3" />
            {results.length} result{results.length === 1 ? '' : 's'}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Thin trigger input shown in the top bar. Renders like a search box but is
 * actually a button — click (or Ctrl/Cmd+K) opens the palette Dialog.
 */
export function GlobalSearchTrigger() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className={cn(
          'relative hidden h-9 w-64 items-center justify-start gap-2 border-input',
          'bg-[rgba(255,255,255,0.02)] px-3 text-sm font-normal text-[var(--color-silver)] md:flex',
          'hover:border-[var(--color-cobalt-bright)] hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-silver-bright)]',
        )}
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">Search…</span>
        <kbd className="rounded border border-border bg-[rgba(255,255,255,0.04)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-silver)]">
          {typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl+K'}
        </kbd>
      </Button>
      {/* Mobile: icon-only search button (shown in bottom-nav or via the menu
          toggle on small screens); keep the dialog mounted so the shortcut
          opens it globally either way. */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="shrink-0 text-[var(--color-silver)] hover:text-[var(--color-silver-bright)] md:hidden"
        aria-label="Search"
      >
        <Search className="h-5 w-5" />
      </Button>
      <GlobalSearch open={open} onOpenChange={setOpen} />
    </>
  )
}
